import * as lamejs from '@breezystack/lamejs';

export interface CompressionResult {
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  compressedFile: File;
}

export interface CompressionProgress {
  progress: number;
  status: string;
}

export class MP3Compressor {
  private encoder: lamejs.Mp3Encoder | null = null;
  private sampleRate: number = 44100;
  private channels: number = 1; // Используем моно для лучшей совместимости
  private bitRate: number = 64; // 64 kbps

  constructor(bitRate: number = 64) {
    this.bitRate = bitRate;
  }

  /**
   * Конвертирует аудио файл в MP3 с заданным битрейтом
   */
  async compressMP3(
    file: File,
    onProgress?: (progress: CompressionProgress) => void
  ): Promise<CompressionResult> {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        
        reader.onload = async (e) => {
          try {
            // Уведомляем о начале процесса
            if (onProgress) {
              onProgress({ progress: 2, status: '📖 Читаем файл...' });
            }
            
            // Небольшая задержка для плавности
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const arrayBuffer = e.target?.result as ArrayBuffer;
            
            if (onProgress) {
              onProgress({ progress: 8, status: '🔧 Инициализируем декодер...' });
            }
            
            // Небольшая задержка для плавности
            await new Promise(resolve => setTimeout(resolve, 50));
            
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            
            // Уведомляем о декодировании
            if (onProgress) {
              onProgress({ progress: 12, status: '🎵 Декодируем аудио...' });
            }
            
            // Декодируем аудио файл
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            
            // Небольшая задержка для плавности
            await new Promise(resolve => setTimeout(resolve, 200));
            
            if (onProgress) {
              onProgress({ progress: 15, status: '📊 Анализируем аудио данные...' });
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            if (onProgress) {
              onProgress({ progress: 18, status: '🎧 Получаем аудио каналы...' });
            }
            
            // Получаем аудио данные
            const leftChannel = audioBuffer.getChannelData(0);
            const rightChannel = audioBuffer.numberOfChannels > 1 ? audioBuffer.getChannelData(1) : leftChannel;
            
            // Используем sampleRate из декодированного аудио вместо фиксированного значения
            // Это критически важно для сохранения правильной скорости воспроизведения
            const actualSampleRate = audioBuffer.sampleRate;
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Уведомляем о конвертации
            if (onProgress) {
              onProgress({ progress: 22, status: '🔄 Конвертируем в моно...' });
            }
            
            if (onProgress) {
              onProgress({ progress: 25, status: '⚙️ Инициализируем MP3 энкодер...' });
            }
            
            await new Promise(resolve => setTimeout(resolve, 150));
            
            // Инициализируем энкодер с правильной частотой дискретизации из исходного файла
            this.encoder = new lamejs.Mp3Encoder(this.channels, actualSampleRate, this.bitRate);
            
            if (onProgress) {
              onProgress({ progress: 27, status: '🔧 Настраиваем параметры кодирования...' });
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            if (onProgress) {
              onProgress({ progress: 28, status: '🔄 Конвертируем аудио данные...' });
            }
            
            // Конвертируем float32 в int16
            const samples = this.convertFloat32ToInt16(leftChannel, rightChannel);
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Кодируем в MP3 по частям
            const mp3Data: Uint8Array[] = [];
            const chunkSize = 1152; // Стандартный размер чанка для MP3 (для моно)
            
            console.log(`Начинаем кодирование: ${samples.length} сэмплов, чанк: ${chunkSize}`);
            
            // Проверяем, что у нас есть данные для кодирования
            if (samples.length === 0) {
              throw new Error('Нет аудио данных для кодирования');
            }
            
            // Вычисляем общее количество чанков
            const totalChunks = Math.ceil(samples.length / chunkSize);
            let processedChunks = 0;
            
            console.log(`Всего чанков для обработки: ${totalChunks}`);
            
            if (onProgress) {
              onProgress({ progress: 30, status: `📋 Подготавливаем кодирование (${totalChunks} чанков)...` });
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            if (onProgress) {
              onProgress({ progress: 32, status: `🚀 Начинаем кодирование MP3 (${totalChunks} чанков)...` });
            }
            
            // Принудительное обновление UI
            await new Promise(resolve => setTimeout(resolve, 50));
            if (onProgress) {
              onProgress({ progress: 32, status: `🚀 Начинаем кодирование MP3 (${totalChunks} чанков)...` });
            }
            
            // Для стерео используем размер чанка, кратный 2
            for (let i = 0; i < samples.length; i += chunkSize) {
              const chunk = samples.slice(i, i + chunkSize);
              if (chunk.length > 0) {
                try {
                  const encoded = this.encoder.encodeBuffer(chunk);
                  // Проверяем, что encoded не undefined и имеет данные
                  if (encoded && encoded.length > 0) {
                    mp3Data.push(encoded);
                    processedChunks++;
                    
                    // Обновляем прогресс в зависимости от размера файла
                    const updateFrequency = totalChunks > 1000 ? 50 : 20; // Для больших файлов реже
                    if (processedChunks % updateFrequency === 0 || processedChunks === totalChunks) {
                      const progress = 32 + Math.round((processedChunks / totalChunks) * 50);
                      
                      // Определяем текущее состояние кодирования
                      let currentState = '';
                      const percentage = Math.round((processedChunks / totalChunks) * 100);
                      
                      if (percentage < 10) {
                        currentState = '🚀 Начинаем кодирование...';
                      } else if (percentage < 25) {
                        currentState = '📝 Кодируем начало файла...';
                      } else if (percentage < 50) {
                        currentState = '⚡ Кодируем первую половину...';
                      } else if (percentage < 75) {
                        currentState = '🔥 Кодируем вторую половину...';
                      } else if (percentage < 90) {
                        currentState = '🏁 Завершаем кодирование...';
                      } else {
                        currentState = '✨ Финальная обработка...';
                      }
                      
                      if (onProgress) {
                        const statusMessage = `${currentState} ${processedChunks}/${totalChunks} чанков (${percentage}%)`;
                        console.log('Отправляем статус:', statusMessage);
                        
                        // Обновляем UI для каждого чанка
                        onProgress({ 
                          progress, 
                          status: statusMessage
                        });
                      }
                    }
                  }
                } catch (encodeError) {
                  console.warn('Ошибка кодирования чанка:', encodeError);
                  // Продолжаем с следующим чанком
                }
              }
            }
            
            console.log(`Обработано чанков: ${processedChunks}, получено данных: ${mp3Data.length}`);
            
            // Уведомляем о финальной обработке
            if (onProgress) {
              onProgress({ progress: 82, status: '🏁 Завершаем кодирование...' });
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Получаем финальную часть
            const mp3End = this.encoder.flush();
            console.log(`Финальная часть: ${mp3End ? mp3End.length : 0} байт`);
            if (mp3End && mp3End.length > 0) {
              mp3Data.push(mp3End);
            }
            
            if (onProgress) {
              onProgress({ progress: 85, status: '📦 Получаем финальные данные...' });
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Проверяем, что у нас есть данные MP3
            if (mp3Data.length === 0) {
              throw new Error('Не удалось закодировать аудио в MP3');
            }
            
            if (onProgress) {
              onProgress({ progress: 88, status: '✅ Проверяем данные MP3...' });
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Уведомляем о сборке файла
            if (onProgress) {
              onProgress({ progress: 92, status: '💾 Создаем сжатый файл...' });
            }
            
            // Принудительное обновление UI
            await new Promise(resolve => setTimeout(resolve, 50));
            if (onProgress) {
              onProgress({ progress: 92, status: '💾 Создаем сжатый файл...' });
            }
            
            // Объединяем данные
            const totalLength = mp3Data.reduce((acc, chunk) => acc + chunk.length, 0);
            console.log(`Общий размер MP3 данных: ${totalLength} байт`);
            
            if (onProgress) {
              onProgress({ progress: 94, status: '📋 Подготавливаем данные...' });
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            if (onProgress) {
              onProgress({ progress: 95, status: '🔗 Объединяем данные...' });
            }
            
            const combinedData = new Uint8Array(totalLength);
            let offset = 0;
            
            for (const chunk of mp3Data) {
              combinedData.set(chunk, offset);
              offset += chunk.length;
            }
            
            if (onProgress) {
              onProgress({ progress: 97, status: '📄 Создаем файл...' });
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const mp3Blob = new Blob([combinedData], { type: 'audio/mp3' });
            
            // Создаем новый файл
            const compressedFile = new File([mp3Blob], file.name, { type: 'audio/mp3' });
            
            const result: CompressionResult = {
              originalSize: file.size,
              compressedSize: compressedFile.size,
              compressionRatio: (file.size - compressedFile.size) / file.size * 100,
              compressedFile
            };
            
            console.log(`Компрессия завершена:`, {
              originalSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
              compressedSize: `${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`,
              compressionRatio: `${result.compressionRatio.toFixed(1)}%`
            });
            
            // Уведомляем о завершении
            if (onProgress) {
              onProgress({ progress: 100, status: 'Компрессия завершена!' });
            }
            
            // Принудительное обновление UI
            await new Promise(resolve => setTimeout(resolve, 50));
            if (onProgress) {
              onProgress({ progress: 100, status: 'Компрессия завершена!' });
            }
            
            resolve(result);
            
          } catch (error) {
            console.error('Ошибка при компрессии:', error);
            reject(error);
          }
        };
        
        reader.onerror = () => {
          reject(new Error('Ошибка чтения файла'));
        };
        
        reader.readAsArrayBuffer(file);
        
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Конвертирует Float32Array в Int16Array для энкодера (моно)
   */
  private convertFloat32ToInt16(leftChannel: Float32Array, rightChannel: Float32Array): Int16Array {
    const length = leftChannel.length;
    const samples = new Int16Array(length);
    
    for (let i = 0; i < length; i++) {
      // Конвертируем float32 (-1.0 to 1.0) в int16 (-32768 to 32767)
      const left = Math.max(-1, Math.min(1, leftChannel[i]));
      const right = Math.max(-1, Math.min(1, rightChannel[i]));
      
      // Для моно используем среднее значение левого и правого канала
      const mono = (left + right) / 2;
      // Используем симметричную формулу для правильной конвертации
      samples[i] = Math.max(-32768, Math.min(32767, Math.round(mono * 32767)));
    }
    
    return samples;
  }

  /**
   * Проверяет, поддерживается ли компрессия в браузере
   */
  static isSupported(): boolean {
    return typeof window !== 'undefined' && 
           (window.AudioContext || (window as any).webkitAudioContext) &&
           typeof lamejs !== 'undefined';
  }

  /**
   * Получает размер файла в читаемом формате
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
} 